import type {
  ApplyResult,
  DecisionInput,
  MatchCandidate,
  MatchRow,
  MatchRun,
  RestorePreview,
} from '@/lib/api/requirement-matches';

/**
 * Pure logic of the match review (T-0264; E14-design §2). No React, no
 * requests: what each row will do, the counts and labels, and the
 * decisions a bulk action sends.
 *
 * Caliber enriches the client's RTM, never rewrites it (ADR-025, 24
 * October 2026): every row goes in with the client's own wording and area;
 * "Add the library item" links the item. A row whose area names no library
 * module goes in Not mapped unless a person picks one.
 */

export const NOT_MAPPED_LABEL = 'Not mapped';

export function plural(n: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? noun : pluralNoun}`;
}

export type MatchGroup = 'same' | 'broader' | 'none' | 'unmatched';

export const GROUP_LABEL: Record<MatchGroup, string> = {
  same: 'Same',
  broader: 'Broader',
  none: 'No match',
  unmatched: 'Not matched yet',
};

/** One line under each group's title. */
export const GROUP_HINT: Record<MatchGroup, string> = {
  same: 'The library item asks for the same thing. Ready to add in the client’s wording, linked to the item; untick a row to skip it.',
  broader:
    'The library item covers the subject but not every detail. Add it anyway, add the row as written, or skip it. Every row keeps the client’s wording.',
  none: 'No library item covers it. Add the row as written, add the nearest library item, or skip it.',
  unmatched: 'The AI has not judged these rows yet. Run the match again to finish them.',
};

/** The verdict chip's tone (StatusChip). */
export const VERDICT_TONE: Record<'same' | 'broader' | 'none', 'healthy' | 'at-risk' | 'unset'> = {
  same: 'healthy',
  broader: 'at-risk',
  none: 'unset',
};

export function rowsOf(run: MatchRun, group: MatchGroup): MatchRow[] {
  return run.groups[group];
}

/** The tabs in order, each with its count; "Not matched yet" only when there are such rows. */
export function groupTabs(
  run: MatchRun,
): Array<{ value: MatchGroup; label: string; count: number }> {
  const tabs: Array<{ value: MatchGroup; label: string; count: number }> = (
    ['same', 'broader', 'none'] as const
  ).map((g) => ({ value: g, label: GROUP_LABEL[g], count: run.groups[g].length }));
  if (run.groups.unmatched.length > 0) {
    tabs.push({
      value: 'unmatched',
      label: GROUP_LABEL.unmatched,
      count: run.groups.unmatched.length,
    });
  }
  return tabs;
}

/** The group to open first: the first one that still needs a person, else Same. */
export function firstGroup(run: MatchRun): MatchGroup {
  const waiting = (g: MatchGroup) =>
    run.groups[g].some((r) => r.requirement === null && r.decision === null && !r.ready);
  if (waiting('broader')) return 'broader';
  if (waiting('none')) return 'none';
  return run.groups.same.length > 0 ? 'same' : run.groups.broader.length > 0 ? 'broader' : 'none';
}

/** A Same row is ticked unless someone skipped it; an added row stays ticked. */
export function isTicked(row: MatchRow): boolean {
  return row.requirement !== null || row.ready;
}

/** The library item a row would add with "Add the library item": its decision, the judge's pick, or the nearest. */
export function libraryChoice(row: MatchRow): MatchCandidate | null {
  if (row.decision_library_item_id) {
    const decided = row.candidates.find((c) => c.library_item_id === row.decision_library_item_id);
    if (decided) return decided;
  }
  return row.chosen ?? row.candidates[0] ?? null;
}

/** Rows the next apply adds, and how many of them are new to the engagement. */
export function readyCounts(run: MatchRun): { ready: number; toAdd: number } {
  const all = [...run.groups.same, ...run.groups.broader, ...run.groups.none];
  const ready = all.filter((r) => r.ready);
  // The judge's item already on the engagement is linked, not added again.
  const linked = (r: MatchRow) =>
    r.already_present &&
    r.decision !== 'add_as_written' &&
    libraryChoice(r)?.library_item_id === r.chosen?.library_item_id;
  return { ready: ready.length, toAdd: ready.filter((r) => !linked(r)).length };
}

export function applyLabel(run: MatchRun): string {
  const { ready, toAdd } = readyCounts(run);
  if (ready === 0) return 'Nothing to add yet';
  return toAdd > 0 ? `Add ${plural(toAdd, 'requirement')}` : 'Apply';
}

/** Everything that is done: added, or skipped, with nothing left to decide. */
export function isFullyApplied(run: MatchRun): boolean {
  return run.status === 'applied' && run.counts.ready === 0;
}

/** Can this row's decision still change? */
export function isEditable(row: MatchRow, run: MatchRun): boolean {
  return row.requirement === null && row.verdict !== null && run.status !== 'matching';
}

export type BulkAction = 'add_all' | 'add_library' | 'add_as_written' | 'skip';

/**
 * The module an Add as written row would go in: the reviewer's, else the
 * one its client area names; null is Not mapped. A row already decided as
 * written with no module stays Not mapped.
 */
export function moduleFor(row: MatchRow): { id: string; name: string } | null {
  if (row.decision === 'add_as_written') return row.decision_module;
  return row.suggested_module;
}

export interface AreaWithoutModule {
  /** The client area as the spreadsheet wrote it, or null for rows with none. */
  area: string | null;
  label: string;
  rows: MatchRow[];
}

/**
 * The open rows Add all as written puts in Not mapped, by client area: no
 * module chosen and none named by the area, and not already set to link the
 * library item or to skip. Largest area first.
 */
export function areasWithoutModule(rows: ReadonlyArray<MatchRow>): AreaWithoutModule[] {
  const byArea = new Map<string, AreaWithoutModule>();
  for (const r of rows) {
    if (r.requirement !== null || r.verdict === null || moduleFor(r)) continue;
    if (r.decision === 'add_library' || r.decision === 'skip') continue;
    const area = r.client_area?.trim() || null;
    const key = area ? area.toLowerCase() : '';
    const entry = byArea.get(key) ?? { area, label: area ?? 'No area', rows: [] };
    entry.rows.push(r);
    byArea.set(key, entry);
  }
  return [...byArea.values()].sort(
    (a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label),
  );
}

/**
 * The decisions a group's bulk action sends, for the rows it can still
 * change: "Add all" on Same puts unticked rows back to preselected (no
 * decision); "Add the library items" takes each row's pick; "Add as
 * written" each row's module, or none (Not mapped) when its area names no
 * module; "Skip all" skips.
 */
export function bulkDecisions(
  rows: ReadonlyArray<MatchRow>,
  action: BulkAction,
): Array<DecisionInput & { row_id: string }> {
  const open = rows.filter((r) => r.requirement === null && r.verdict !== null);
  switch (action) {
    case 'add_all':
      return open.filter((r) => r.decision !== null).map((r) => ({ row_id: r.id, decision: null }));
    case 'add_library':
      return open.flatMap((r) => {
        const pick = libraryChoice(r);
        return pick
          ? [
              {
                row_id: r.id,
                decision: 'add_library' as const,
                library_item_id: pick.library_item_id,
              },
            ]
          : [];
      });
    case 'add_as_written':
      return open.map((r) => {
        const module = moduleFor(r);
        return module
          ? { row_id: r.id, decision: 'add_as_written' as const, module_id: module.id }
          : { row_id: r.id, decision: 'add_as_written' as const };
      });
    case 'skip':
      return open.map((r) => ({ row_id: r.id, decision: 'skip' as const }));
  }
}

/** "Matching 365 rows", then "Matched 120 of 365 rows". */
export function progressText(run: MatchRun): string {
  const { rows, matched } = run.counts;
  return matched === 0
    ? `Matching ${plural(rows, 'row')}`
    : `Matched ${matched.toLocaleString()} of ${plural(rows, 'row')}`;
}

/** The header line: what the review holds and what is left. */
export function summaryText(run: MatchRun): string {
  const c = run.counts;
  const parts = [
    `${c.same.toLocaleString()} same`,
    `${c.broader.toLocaleString()} broader`,
    `${c.none.toLocaleString()} no match`,
  ];
  if (c.added > 0) parts.push(`${c.added.toLocaleString()} added`);
  if (c.undecided > 0) parts.push(`${c.undecided.toLocaleString()} to decide`);
  return parts.join(' · ');
}

/** The toast after an apply, from the server's own counts. */
export function applyResultMessage(result: ApplyResult): string {
  const parts: string[] = [];
  const added = result.added + result.added_as_written;
  parts.push(`${plural(added, 'requirement')} added`);
  if (result.added_as_written > 0)
    parts.push(`${result.added_as_written.toLocaleString()} as written`);
  if (result.restored > 0) parts.push(`${result.restored.toLocaleString()} restored`);
  if (result.already_present > 0) {
    parts.push(`${result.already_present.toLocaleString()} already on this engagement`);
  }
  if (result.not_applied > 0) {
    parts.push(
      `${result.not_applied.toLocaleString()} left out: the library item or module was retired`,
    );
  }
  return `${parts.join(', ')}.`;
}

/** What the row will become, in words, for the right-hand side of a row. */
export function landsIn(row: MatchRow): string {
  if (row.requirement) {
    return row.requirement.status === 'removed'
      ? `Added as ${row.requirement.number}, removed since`
      : `Added as ${row.requirement.number}`;
  }
  if (row.decision === 'skip') return 'Skipped';
  if (row.decision === 'add_as_written') {
    return row.decision_module
      ? `Adds the row as written to ${row.decision_module.name}`
      : `Adds the row as written, ${NOT_MAPPED_LABEL}`;
  }
  if (row.ready) {
    const pick = libraryChoice(row);
    if (row.already_present && pick?.library_item_id === row.chosen?.library_item_id) {
      return 'Links to the requirement already here';
    }
    return pick
      ? `Adds the row in the client’s wording, linked to ${pick.number} (${pick.module_name})`
      : 'Adds the row, linked to the library item';
  }
  return 'Not decided';
}

/**
 * A candidate's score in words: code's cosine for a match by meaning, the
 * word overlap of a wording match. Null when the row stored none.
 */
export function scoreText(c: Pick<MatchCandidate, 'score' | 'match_kind'>): string | null {
  if (c.score === null || c.score === undefined) return null;
  if (c.match_kind === 'exact') return 'Same wording';
  if (c.match_kind === 'wording') return `Word overlap ${c.score.toFixed(2)}`;
  return `Meaning ${c.score.toFixed(2)}`;
}

/** The lines of the restore card, from the server's preview. */
export function restoreLines(p: RestorePreview): string[] {
  const lines: string[] = [];
  if (p.text > 0) {
    lines.push(
      `${plural(p.text, 'requirement')} get the client’s own wording back. The library item stays linked, and its wording is offered as a suggestion.`,
    );
  }
  if (p.client_area > 0) {
    lines.push(`${plural(p.client_area, 'requirement')} get their client area.`);
  }
  if (p.not_mapped > 0) {
    lines.push(
      `${plural(p.not_mapped, 'requirement')} leave the module a vote of library items chose for an area no module is named for, and become ${NOT_MAPPED_LABEL}: ${p.not_mapped_by_area
        .map((a) => `${a.area}, ${a.count.toLocaleString()} from ${a.module_name}`)
        .join('; ')}.`,
    );
  }
  if (p.text_kept_edited > 0) {
    lines.push(
      `${plural(p.text_kept_edited, 'requirement')} reworded by someone since keep ${p.text_kept_edited === 1 ? 'its' : 'their'} words.`,
    );
  }
  return lines;
}
